import { type InputHTMLAttributes, type Ref } from 'react';
import { CountryData, defaultCountries, parseCountry, usePhoneInput } from 'react-international-phone';

import { CountryPicker } from './CountryPicker';
import { type PhoneInputOutput } from './types';
import { TextInput } from '../TextInput';

export type PhoneInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  label?: string;
  errorMessage?: string;
  value?: string;
  onChange?: (_data: PhoneInputOutput) => void;
  rhfRef?: Ref<HTMLInputElement>;
};

function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]): React.RefCallback<T> {
  return value => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

// Fix issue with AR phone numbers
// https://github.com/ybrusentsov/react-international-phone/issues/167
const parsedCountries = defaultCountries.map(country => {
  const { iso2 } = parseCountry(country);

  if (iso2 === 'ar') {
    const newCountryAr: CountryData = [
      'Argentina',
      'ar',
      '54',
      { default: '.. .... ....', '/^9/': '. .. .... ....', '/^8/': '... ... ....', '/^6/': '... ... ....' },
      0,
    ];
    return newCountryAr;
  } else {
    return country;
  }
});

export const PhoneInput = ({ label, id, errorMessage, value, onChange, rhfRef, ...rest }: PhoneInputProps) => {
  const { phone, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: 'fr',
    value: value ?? '',
    countries: parsedCountries,
    onChange: data => onChange?.({ country: data.country.iso2, phone: data.phone }),
  });

  const combinedRef = mergeRefs(inputRef, rhfRef);

  return (
    <TextInput
      {...rest}
      type="text"
      ref={combinedRef}
      id={id}
      label={label}
      errorMessage={errorMessage}
      value={phone}
      onChange={handlePhoneValueChange}
      className="pl-15 pr-3"
      inputMode="tel"
    >
      <CountryPicker className="absolute left-0 bottom-0 top-0" country={country.iso2} setCountry={setCountry} />
    </TextInput>
  );
};
